import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import classes from './TextWidget.module.css';
import { componentsTestIds } from '@components/testids';

/**
 * TV1-1930 (S15) — the Text / Markdown widget. The first DATALESS widget.
 *
 * No cube, no measures, no Cube query, no fetch. It exists so a report can carry
 * narrative: a heading, a paragraph of commentary, a caveat next to a chart — and,
 * from TV1-1938 onwards, an LLM-authored summary.
 *
 * ## Security: safe BY DEFAULT, and kept that way on purpose
 *
 * react-markdown v10 does **not** render raw HTML unless you explicitly add
 * `rehype-raw`. So:
 *
 *  - **DO NOT add `rehype-raw`.** It is the single switch that turns this
 *    component into an HTML injection sink.
 *  - **DO NOT use `dangerouslySetInnerHTML`.** There are ZERO occurrences of it
 *    in web-app today. Do not be the first.
 *
 * The one residual vector is a `javascript:` URL in an ordinary markdown link —
 * `[click me](javascript:alert(1))`. react-markdown's default `urlTransform`
 * strips it, but "the library says it does" is not a control we own, so
 * `TextWidget.test.tsx` **asserts** it: it renders exactly that payload and
 * checks no `javascript:` href reaches the DOM. The AC says "no XSS", and
 * TV1-1938 makes this content LLM-authored and therefore UNTRUSTED — a test is
 * the only version of that claim that stays true after the next dependency bump.
 *
 * ## GFM
 *
 * `remark-gfm` is on because an LLM writing a narrative block **will** emit a
 * table, and base CommonMark renders one as a row of literal pipe characters. It
 * also buys strikethrough, task lists and autolinks. It adds no HTML-rendering
 * capability, so it does not widen the surface above.
 */

export type TextWidgetProps = {
  /** The markdown source. Untrusted — see the security note above. */
  content?: string;
};

export const TextWidget = ({ content }: TextWidgetProps ) => {
  const { t } = useTranslation( 'widgets' );

  if ( !content?.trim()) {
    return (
      <div className={classes.empty} data-testid={componentsTestIds.textWidgetEmpty}>
        {t( 'widget_text_empty', 'This text block is empty. Edit the widget to add content.' )}
      </div>
    );
  }

  return (
    <div className={classes.container} data-testid={componentsTestIds.textWidget}>
      {/* No `rehypePlugins`. No `rehype-raw`. Raw HTML in the source is rendered
          as inert text, which is exactly what we want from untrusted content. */}
      <Markdown remarkPlugins={[remarkGfm]}>{content}</Markdown>
    </div>
  );
};
